import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BaseModal from '@components/common/BaseModal/BaseModal.component'
import Stepper from '@components/common/Stepper/Stepper.component'
import './ImageViewerModalStyle.css'
import SelectImages from './SelectImages/SelectImages.component'
import ReportType from './ReportType/ReportType.component'
import PrintPreview from './PrintPreview/PrintPreview.component'
import Button from '@components/common/Button/Button.component'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { getFileThumbnailUrl } from '@services/api/folder.api'
import type {
  ReportImage,
  ReportConfig,
  ClinicInfo,
  PatientInfo,
} from './reportTypes'
import { logger } from '@utils/safeLogger'
type ImageViewerModalProps = {
  show: boolean
  handleClose: () => void
  patient?: any
  clinic?: any
  dicomFiles?: any[]
}

const STEPS = ['Select images', 'Report type', 'Review']

const DEFAULT_CATEGORIES: Record<string, boolean> = {
  'Caries and Cavities': true,
  Periodontal: true,
  Endodontic: true,
  Radiolucency: true,
  Lesions: true,
  'Damaged/Missing': true,
  Recommendations: true,
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  show,
  handleClose,
  patient,
  clinic,
  dicomFiles,
}) => {
  const [step, setStep] = useState(1)
  const [reportConfig, setReportConfig] = useState<ReportConfig>({
    reportType: 'Full',
    categories: { ...DEFAULT_CATEGORIES },
  })
  const [isPrinting, setIsPrinting] = useState(false)
  const printRef = useRef<HTMLDivElement>(null)

  const initialImages: ReportImage[] = useMemo(() => {
    if (dicomFiles && dicomFiles.length > 0) {
      return dicomFiles.map((f: any, i: number) => ({
        id: f.file_id || `img-${i}`,
        imageUrl: f.preview_path || f.thumbnail_path || '',
        fileName: f.file_name || 'X-Ray File.dcm',
        fileSize: f.file_size
          ? `${(f.file_size / (1024 * 1024)).toFixed(0)} MB`
          : '12 MB',
        uploadDate: f.created_at
          ? new Date(f.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })
          : 'Oct 20, 2025 - 8:30 AM',
        isSelected: i === 0,
        isFavorite: false,
      }))
    }
    return [
      {
        id: 'img-1',
        imageUrl: '',
        fileName: 'X-Ray File.dcm',
        fileSize: '12 MB',
        uploadDate: 'Oct 20, 2025 - 8:30 AM',
        isSelected: false,
        isFavorite: false,
      },
      {
        id: 'img-2',
        imageUrl: '',
        fileName: 'X-Ray File.dcm',
        fileSize: '12 MB',
        uploadDate: 'Oct 20, 2025 - 8:30 AM',
        isSelected: true,
        isFavorite: false,
      },
      {
        id: 'img-3',
        imageUrl: '',
        fileName: 'X-Ray File.dcm',
        fileSize: '12 MB',
        uploadDate: 'Oct 20, 2025 - 8:30 AM',
        isSelected: false,
        isFavorite: false,
      },
      {
        id: 'img-4',
        imageUrl: '',
        fileName: 'X-Ray File.dcm',
        fileSize: '12 MB',
        uploadDate: 'Oct 20, 2025 - 8:30 AM',
        isSelected: false,
        isFavorite: false,
      },
    ]
  }, [dicomFiles])

  const [images, setImages] = useState<ReportImage[]>(initialImages)

  // Re-sync local state whenever the source files change (e.g. modal reopens
  // for a different patient or after new uploads).
  useEffect(() => {
    setImages(initialImages)
  }, [initialImages])

  // Mirror the Files page "View File" pattern: rather than relying on the
  // bulk-signed `preview_path` URLs from the folder listing (which can come
  // back unsigned in staging when the listing-time signing call fails), fetch
  // a fresh, short-lived signed thumbnail URL per file when the modal opens.
  // Falls back to whatever `imageUrl` the listing already provided.
  useEffect(() => {
    if (!show) return
    if (!dicomFiles || dicomFiles.length === 0) return

    let cancelled = false
    const ids = dicomFiles
      .map((f: any) => f.file_id)
      .filter((id: unknown): id is string => typeof id === 'string' && !!id)

    void Promise.all(
      ids.map(async (fileId) => {
        const res = await getFileThumbnailUrl(fileId)
        return { fileId, url: res?.data?.url || null }
      }),
    ).then((results) => {
      if (cancelled) return
      const urlByFileId = new Map(
        results.filter((r) => !!r.url).map((r) => [r.fileId, r.url as string]),
      )
      if (urlByFileId.size === 0) return
      setImages((prev) =>
        prev.map((img) => {
          const fresh = urlByFileId.get(img.id)
          return fresh ? { ...img, imageUrl: fresh } : img
        }),
      )
    })

    return () => {
      cancelled = true
    }
  }, [show, dicomFiles])

  const selectedImages = useMemo(
    () => images.filter((img) => img.isSelected),
    [images],
  )

  const clinicInfo: ClinicInfo = useMemo(
    () => ({
      name: clinic?.clinic_name || 'XYZ Clinic',
      address: clinic?.address || '7799 Green Bell Street',
      city: clinic?.city
        ? `${clinic.city}, ${clinic.state || 'CA'} ${clinic.zip || '91001'}`
        : 'San Diego, CA 91001',
      logoUrl: clinic?.logo_url,
    }),
    [clinic],
  )

  const patientInfo: PatientInfo = useMemo(() => {
    const dob = patient?.date_of_birth
    let age = 31
    if (dob) {
      const birthDate = new Date(dob)
      const today = new Date()
      age = today.getFullYear() - birthDate.getFullYear()
      const m = today.getMonth() - birthDate.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--
    }

    return {
      name: patient
        ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim()
        : 'John Doe',
      patientId: patient?.patient_id_display || patient?.patient_id || 'XCP45',
      gender: patient?.gender || 'Male',
      age,
      scanDate: new Date().toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      }),
      scanType: 'Panoramic Dental X-ray (D0330)',
    }
  }, [patient])

  const handleToggleImage = useCallback((id: string) => {
    setImages((prev) =>
      prev.map((img) =>
        img.id === id ? { ...img, isSelected: !img.isSelected } : img,
      ),
    )
  }, [])

  const handleToggleFavorite = useCallback((id: string) => {
    setImages((prev) =>
      prev.map((img) =>
        img.id === id ? { ...img, isFavorite: !img.isFavorite } : img,
      ),
    )
  }, [])

  const handleReportTypeChange = useCallback((type: 'Full' | 'Summary') => {
    setReportConfig((prev) => ({ ...prev, reportType: type }))
  }, [])

  const handleCategoryToggle = useCallback((category: string) => {
    setReportConfig((prev) => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: !prev.categories[category],
      },
    }))
  }, [])

  const handleNext = (): void => {
    setStep((prev) => Math.min(prev + 1, 3))
  }

  const handleBack = (): void => {
    setStep((prev) => Math.max(prev - 1, 1))
  }

  const handlePrint = useCallback(async () => {
    if (!printRef.current) return
    setIsPrinting(true)
    try {
      // Wait for every <img> inside the print container to finish loading
      // before snapshotting. Without this, clicking Print before the freshly
      // signed thumbnail URLs have downloaded produces a PDF with blank or
      // missing X-ray images on staging.
      const imgs = Array.from(
        printRef.current.querySelectorAll('img'),
      ) as HTMLImageElement[]
      await Promise.all(
        imgs.map(
          (img) =>
            new Promise<void>((resolve) => {
              if (img.complete && img.naturalWidth > 0) return resolve()
              img.addEventListener('load', () => resolve(), { once: true })
              img.addEventListener('error', () => resolve(), { once: true })
            }),
        ),
      )

      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      })
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)
      pdf.save(
        `Radiography_Report_${patientInfo.name.replace(/\s+/g, '_')}_${patientInfo.scanDate.replace(/\//g, '-')}.pdf`,
      )
      handleClose()
      setStep(1)
      setReportConfig({
        reportType: 'Full',
        categories: { ...DEFAULT_CATEGORIES },
      })
      setImages(initialImages)
    } catch (err) {
      logger.error('Failed to generate PDF:', err)
      window.print()
    } finally {
      setIsPrinting(false)
    }
  }, [patientInfo, handleClose, initialImages])

  const handleModalClose = (): void => {
    handleClose()
    setStep(1)
    setReportConfig({
      reportType: 'Full',
      categories: { ...DEFAULT_CATEGORIES },
    })
    setImages(initialImages)
  }

  const renderStepContent = (): React.ReactElement => {
    switch (step) {
      case 1:
        return (
          <SelectImages
            images={images}
            onToggleSelect={handleToggleImage}
            onToggleFavorite={handleToggleFavorite}
          />
        )
      case 2:
        return (
          <ReportType
            reportConfig={reportConfig}
            onReportTypeChange={handleReportTypeChange}
            onCategoryToggle={handleCategoryToggle}
            clinicInfo={clinicInfo}
            patientInfo={patientInfo}
            selectedImages={selectedImages}
          />
        )
      case 3:
        return (
          <PrintPreview
            ref={printRef}
            clinicInfo={clinicInfo}
            patientInfo={patientInfo}
            selectedImages={selectedImages}
            reportConfig={reportConfig}
            fullWidth
          />
        )
      default:
        return <></>
    }
  }

  const renderFooter = (): React.ReactElement => (
    <div className="ivm-footer">
      {step > 1 && (
        <Button className="ivm-btn-cancel" onClick={handleBack}>
          Cancel
        </Button>
      )}
      {step < 3 && (
        <Button
          className="ivm-btn-next"
          onClick={handleNext}
          disabled={step === 1 && selectedImages.length === 0}
        >
          Next
        </Button>
      )}
      {step === 3 && (
        <Button
          className="ivm-btn-print"
          onClick={handlePrint}
          disabled={isPrinting}
        >
          {isPrinting ? 'Generating...' : 'Print'}
        </Button>
      )}
    </div>
  )

  return (
    <BaseModal
      className="image-viewer-modal-container"
      show={show}
      handleClose={handleModalClose}
      title="Report preview"
      footer={renderFooter()}
    >
      <div className="ivm-stepper-container">
        <Stepper steps={STEPS} currentStep={step - 1} />
      </div>
      <div className="ivm-content">{renderStepContent()}</div>
    </BaseModal>
  )
}

export default ImageViewerModal
